/**
 * InstructorProfile Component
 *
 * Displays complete instructor profile with all details
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import type { InstructorProfile as InstructorProfileData } from '../../services/instructorService';

interface InstructorProfileProps {
  instructor: InstructorProfileData;
  className?: string;
}

export function InstructorProfile({ instructor, className = '' }: InstructorProfileProps) {
  const { basic_info, location, certification, professional, materials, providers } = instructor;
  const [imageError, setImageError] = useState(false);

  // Build location string
  const locationParts = [location.city, location.state, location.country].filter(Boolean);
  const locationString = locationParts.join(', ') || 'No especificada';

  // Photo URL from instructor data
  const photoUrl = basic_info.photo;

  // Reset image error state when instructor photo changes
  useEffect(() => {
    setImageError(false);
  }, [photoUrl]);

  // DEBUG: Log photo URL for debugging 404 errors
  console.log('[InstructorProfile] Instructor:', basic_info.name);
  console.log('[InstructorProfile] Photo URL:', photoUrl);
  console.log('[InstructorProfile] Has photo in data:', !!basic_info.photo);
  console.log('[InstructorProfile] Image error state:', imageError);

  return (
    <div class={`max-w-5xl mx-auto ${className}`}>
      {/* Header Section */}
      <div class="bg-white rounded-lg shadow-lg overflow-hidden mb-6">
        <div class="md:flex">
          {/* Photo */}
          <div class="md:flex-shrink-0 h-64 w-full md:w-64 bg-gray-200">
            {photoUrl && !imageError ? (
              <img
                src={photoUrl}
                alt={basic_info.name}
                class="h-full w-full object-cover"
                onError={(e) => {
                  console.error('[InstructorProfile] Failed to load photo:', photoUrl);
                  console.error('[InstructorProfile] Image error event:', e);
                  setImageError(true);
                }}
              />
            ) : (
              <div class="h-full w-full flex items-center justify-center">
                <svg
                  class="w-32 h-32 text-gray-400"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div class="p-8 flex-1">
            <div class="flex items-start justify-between">
              <div>
                <h1 class="text-3xl font-bold text-gray-900 mb-2">
                  {basic_info.name}
                </h1>

                {/* Verified Badge */}
                {basic_info.verified && (
                  <div class="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold mb-3">
                    <svg
                      class="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Instructor Verificado
                  </div>
                )}
              </div>
            </div>

            {/* Location */}
            <div class="flex items-center gap-2 text-gray-600 mb-4">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{locationString}</span>
            </div>

            {/* Contact */}
            {(basic_info.email || basic_info.phone) && (
              <div class="space-y-2">
                {basic_info.email && (
                  <div class="flex items-center gap-2 text-sm text-gray-700">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <a href={`mailto:${basic_info.email}`} class="hover:text-blue-600 transition">
                      {basic_info.email}
                    </a>
                  </div>
                )}
                {basic_info.phone && (
                  <div class="flex items-center gap-2 text-sm text-gray-700">
                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href={`tel:${basic_info.phone}`} class="hover:text-blue-600 transition">
                      {basic_info.phone}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content Sections */}
      <div class="space-y-6">
        {/* Certification Section */}
        <div class="bg-white rounded-lg shadow-md p-6">
          <h2 class="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            Certificación
          </h2>
          <div class="grid md:grid-cols-3 gap-4">
            <div>
              <p class="text-sm font-medium text-gray-500">Escuela</p>
              <p class="text-base text-gray-900">{certification.school || 'No especificada'}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-500">Año</p>
              <p class="text-base text-gray-900">{certification.year || 'No especificado'}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-500">Maestro/a</p>
              <p class="text-base text-gray-900">{certification.master || 'No especificado'}</p>
            </div>
          </div>
        </div>

        {/* Professional Section */}
        {(professional.specialties || professional.story || professional.nivel_de_experiencia_en_fuego) && (
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Información Profesional
            </h2>

            {professional.is_professional && (
              <div class="mb-4 inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                Profesional del desarrollo personal
              </div>
            )}

            {professional.nivel_de_experiencia_en_fuego && (
              <div class="mb-4">
                <p class="text-sm font-medium text-gray-500 mb-1">Experiencia con Fuego</p>
                <div class="inline-flex items-center gap-2 px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clip-rule="evenodd" />
                  </svg>
                  {professional.nivel_de_experiencia_en_fuego}
                </div>
              </div>
            )}

            {professional.specialties && Array.isArray(professional.specialties) && professional.specialties.length > 0 && (
              <div class="mb-4">
                <p class="text-sm font-medium text-gray-500 mb-2">Especialidades</p>
                <div class="flex flex-wrap gap-2">
                  {professional.specialties.map((specialty) => (
                    <span
                      key={specialty}
                      class="inline-block px-3 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {professional.story && (
              <div>
                <p class="text-sm font-medium text-gray-500 mb-2">Historia y Experiencia</p>
                <p class="text-gray-700 whitespace-pre-line">{professional.story}</p>
              </div>
            )}
          </div>
        )}

        {/* Materials & Providers Section */}
        <div class="grid md:grid-cols-2 gap-6">
          {/* Materials */}
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              Material DAI Disponible
            </h2>
            <ul class="space-y-2">
              {materials.has_crystals_bed && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Cama de cristales
                </li>
              )}
              {materials.has_arrow && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Flechas
                </li>
              )}
              {materials.has_steel_bars && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Barras de acero
                </li>
              )}
              {materials.has_concrete_blocks && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Bloques de hormigón
                </li>
              )}
              {materials.has_other && materials.other_description?.trim() && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  {materials.other_description}
                </li>
              )}
              {!materials.has_crystals_bed && !materials.has_arrow && !materials.has_steel_bars && !materials.has_concrete_blocks && !materials.other_description?.trim() && (
                <li class="text-sm text-gray-500">No especificado</li>
              )}
            </ul>
          </div>

          {/* Providers */}
          <div class="bg-white rounded-lg shadow-md p-6">
            <h2 class="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              Proveedor de Materiales
            </h2>
            <ul class="space-y-2">
              {providers.provides_wood && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Leña
                </li>
              )}
              {providers.provides_tepe && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Tepe
                </li>
              )}
              {providers.provides_iron_bar && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Barra de hierro
                </li>
              )}
              {providers.provides_arrows && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Flechas
                </li>
              )}
              {providers.provides_wood_board && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Tabla de madera
                </li>
              )}
              {providers.provides_concrete_block && (
                <li class="flex items-center gap-2 text-sm text-gray-700">
                  <svg class="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                  </svg>
                  Bloque de hormigón
                </li>
              )}
              {!providers.provides_wood && !providers.provides_tepe && !providers.provides_iron_bar &&
               !providers.provides_arrows && !providers.provides_wood_board && !providers.provides_concrete_block && (
                <li class="text-sm text-gray-500">No proporciona materiales</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
